import imageCompression from 'browser-image-compression';
import md5 from 'md5';
import exifr from 'exifr';
import uploadConstants from '../config/uploadConstants.json';

const { COMPRESSION_MAX_DIMENSION_PX, COMPRESSION_MAX_SIZE_MB, COMPRESSION_INITIAL_QUALITY_PERCENT, COMPRESSION_TIMEOUT_MS } = uploadConstants;

const ACCEPTED_IMAGE_EXTENSIONS = /\.(jpe?g|png|heic|heif)$/i;
const HEIC_EXTENSIONS = /\.(heic|heif)$/i;
const HEIC_MIME_TYPES = new Set(['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence']);

export function isCompressibleEventImage(file) {
  const type = String(file?.type || '').toLowerCase();
  return Boolean(
    file &&
    (ACCEPTED_IMAGE_EXTENSIONS.test(file.name || '') ||
      type === 'image/jpeg' ||
      type === 'image/png' ||
      HEIC_MIME_TYPES.has(type))
  );
}

export function compressedImageName(name) {
  const safe = String(name || 'photo').replace(/[\\/]/g, '_');
  return safe.replace(/\.[^.]+$/, '') + '.jpg';
}

const SUFFIX_CHARS = 'abcdefghijklmnopqrstuvwxyz0123456789';

function randomSuffix(length = 4) {
  let out = '';
  for (let i = 0; i < length; i += 1) {
    out += SUFFIX_CHARS[Math.floor(Math.random() * SUFFIX_CHARS.length)];
  }
  return out;
}

// Appends a random 4-char alphanumeric suffix before the extension so that
// photos with identical filenames (e.g. "DSC_3000.jpg" from different cameras
// or folders) don't collide and overwrite each other once uploaded.
export function uniqueCompressedImageName(name) {
  const safe = String(name || 'photo').replace(/[\\/]/g, '_');
  const base = safe.replace(/\.[^.]+$/, '');
  return `${base}_${randomSuffix()}.jpg`;
}

// Mirrors the backend's _safe_upload_path (cm-backend/lambda/routes/media.py) exactly.
// The /original-upload-urls response echoes back this same cleaned string as
// `relative_path`; if the client used a different (unsanitized) string as its own
// upload-URL lookup key, every file with a "special" character in its original name
// (spaces, parentheses, unicode - extremely common in camera/phone filenames) would
// silently fail the lookup and never get uploaded, with no visible error at PUT time.
export function sanitizeUploadRelativePath(value, fallback) {
  const raw = String(value || fallback || '').replace(/\\/g, '/').trim().replace(/^\/+/, '');
  const parts = raw
    .split('/')
    .map((part) => part.trim().replace(/[^A-Za-z0-9._-]/g, '_'))
    .filter((part) => part && part !== '.' && part !== '..');
  if (!parts.length) {
    parts.push(String(fallback || `photo_${randomSuffix(8)}.jpg`));
  }
  // The face-grouping pipeline (photo_id_from_relative_path in
  // face_group/main.py) derives photo_id from this same filename's stem, but
  // further replaces any INTERIOR '.' with '_' (a stem like "Screenshot
  // 2024-03-15 at 10.30.45" - already sanitized down to
  // "Screenshot_2024-03-15_at_10.30.45" above - still has one). Stripping
  // that here too means this relative_path's own stem always matches the
  // photo_id the pipeline will actually assign, instead of only agreeing
  // for filenames with a single dot. _safe_upload_path (media.py) must
  // apply this identically - see that function's own comment.
  const lastIdx = parts.length - 1;
  const stem = parts[lastIdx].replace(/\.[^.]+$/, '').replace(/\./g, '_') || 'photo';
  parts[lastIdx] = `${stem}.jpg`;
  return parts.join('/');
}

// MD5 of the file's RAW, pre-compression bytes - must be called before
// compressEventImage/compressEventImagesInChunks touches the file, since
// compression is lossy and irreversible; there is no way to recover the
// original's hash from the compressed copy afterward. Paired with the
// original (pre-sanitization) filename and posted to /uc-file-metadata
// alongside that batch's upload-URL request - see each upload engine's own
// call site for why (durable original-file identity independent of the
// compressed copy's sanitized/suffixed name).
export async function computeUcMd5Hash(file) {
  const buf = await file.arrayBuffer();
  return md5(new Uint8Array(buf));
}

// Small, JSON-safe set of EXIF tags worth surfacing in the gallery - deliberately
// excludes MakerNote/UserComment/the embedded EXIF thumbnail (large binary blobs).
const EXIF_TAG_KEYS = ['Make', 'Model', 'LensModel', 'Orientation', 'FNumber', 'ExposureTime', 'ISO', 'FocalLength', 'Software'];

// Fallback when there's no usable EXIF date (screenshots, PNGs, re-exports,
// some HEIC files) - the browser File's lastModified timestamp. Not a true
// "creation date" (browsers/OS mostly don't expose one), but a reasonable
// stand-in so a dateless photo doesn't just sink to the bottom of every
// newest-first sort with no ordering signal at all.
function fallbackCapturedAt(file) {
  const ts = file?.lastModified;
  if (typeof ts !== 'number' || ts <= 0) return null;
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

// Reads capture date + a small EXIF tag set from the file's RAW bytes - must be
// called before compressEventImage/compressEventImagesInChunks touches the file
// (browser-image-compression re-encodes onto a canvas, which drops EXIF entirely),
// same "call before compression" rule as computeUcMd5Hash above. Never throws:
// a photo with no/corrupt EXIF (screenshots, PNGs, re-exports) falls back to
// the file's lastModified timestamp instead (see fallbackCapturedAt).
export async function extractCaptureMetadata(file) {
  try {
    const output = await exifr.parse(file, { gps: true });
    if (!output) return { capturedAt: fallbackCapturedAt(file), exifTags: null };
    const dt = output.DateTimeOriginal || output.DateTime || output.CreateDate;
    const capturedAt = dt instanceof Date && !Number.isNaN(dt.getTime()) ? dt.toISOString() : fallbackCapturedAt(file);
    const exifTags = {};
    for (const key of EXIF_TAG_KEYS) {
      if (output[key] !== undefined && output[key] !== null) exifTags[key] = output[key];
    }
    if (typeof output.latitude === 'number' && typeof output.longitude === 'number') {
      exifTags.GPSLatitude = output.latitude;
      exifTags.GPSLongitude = output.longitude;
    }
    return { capturedAt, exifTags: Object.keys(exifTags).length ? exifTags : null };
  } catch {
    return { capturedAt: fallbackCapturedAt(file), exifTags: null };
  }
}

function isHeicImage(file) {
  return HEIC_EXTENSIONS.test(file?.name || '') || HEIC_MIME_TYPES.has(String(file?.type || '').toLowerCase());
}

async function convertHeicToJpeg(file) {
  const { default: heic2any } = await import('heic2any');
  const converted = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.9,
  });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  return new File([blob], compressedImageName(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified || Date.now(),
  });
}

export async function compressEventImage(file) {
  const jpegReadyFile = isHeicImage(file) ? await convertHeicToJpeg(file) : file;
  const compressed = await imageCompression(jpegReadyFile, {
    maxWidthOrHeight: COMPRESSION_MAX_DIMENSION_PX,
    maxSizeMB: COMPRESSION_MAX_SIZE_MB,
    initialQuality: COMPRESSION_INITIAL_QUALITY_PERCENT / 100,
    fileType: 'image/jpeg',
    useWebWorker: true,
  });
  return new File([compressed], compressedImageName(file.name), {
    type: 'image/jpeg',
    lastModified: file.lastModified || Date.now(),
  });
}

const DEFAULT_COMPRESSION_CHUNK_SIZE = 6;

// browser-image-compression's useWebWorker:true path (see compressEventImage)
// can, in some hosting contexts, never settle at all rather than reject - a
// Web Worker that silently fails to spin up (confirmed as a real risk in the
// headless WebView FolderSyncForegroundService uses for backgrounded
// upload-link auto-sync, which has no visible surface) leaves that call's
// promise pending forever. Plain `.catch(() => file)` (below) only guards
// against a genuine rejection; a hang needs a race against a timeout instead,
// so one stuck file can't block the entire batch (and hence the upload that
// follows) indefinitely - falling back to that file's original, uncompressed
// bytes is far better than never uploading it at all.
function compressWithTimeout(file) {
  return new Promise((resolve) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      resolve(file);
    }, COMPRESSION_TIMEOUT_MS);
    compressEventImage(file).then((result) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(result);
    }).catch(() => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve(file);
    });
  });
}

// Compresses a batch in small concurrent chunks rather than firing every file
// at once - a 100-photo auto-sync batch spinning up 100 simultaneous Web
// Worker compressions at once is heavier than it needs to be on lower-end
// devices; chunking keeps only a handful of images being processed in memory
// at any moment while still overlapping work within each chunk. Falls back to
// each file's original bytes if compression itself throws OR hangs past
// COMPRESSION_TIMEOUT_MS for that file (see compressWithTimeout), same as a
// single compressEventImage call would be expected to degrade.
// `onProgress({ completed, total })`, if given, fires after each chunk so
// callers can drive a determinate progress bar.
export async function compressEventImagesInChunks(files, chunkSize = DEFAULT_COMPRESSION_CHUNK_SIZE, onProgress) {
  const results = [];
  for (let i = 0; i < files.length; i += chunkSize) {
    const chunk = files.slice(i, i + chunkSize);
    const compressedChunk = await Promise.all(chunk.map((file) => compressWithTimeout(file)));
    results.push(...compressedChunk);
    onProgress?.({ completed: results.length, total: files.length });
  }
  return results;
}
